import React, { type ChangeEvent, useMemo, useRef, useState } from 'react';

import omit from 'lodash/omit';
import { useSnackbar } from 'notistack';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

import { useGetAccountAssets } from '~/hooks/apis/offers/useGetAccountAssets';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import { formatDateISO } from '~/services/TimeService';

import {
  useBlackoutsContext,
  useInclusionDraftsContext,
  usePackageOptionsContext,
  usePricingToolSession,
  usePropertySetupContext,
} from '../PricingToolSessionContext';
import CopySectionOption from '../copyFromPrevious/CopySectionOption';
import { type CopySectionDetails, replaceWarning } from '../copyFromPrevious/copyFromPreviousModel';
import { findReusableInclusionAsset } from '../tabs/inclusions/inclusionsModel';
import { DEFAULT_OPTION_STATE, collectRoomAssets } from '../tabs/packageOptions/packageOptionsModel';
import { replaceWithPreviousPackages } from '../tabs/packageOptions/previousDealOptions';

import ImportInclusionsStep from './ImportInclusionsStep';
import ImportRoomsStep from './ImportRoomsStep';
import {
  IMPORT_SECTIONS,
  IMPORT_STEP_LABELS,
  type ImportSectionKey,
  type ImportStep,
  type InclusionAssetChoices,
  blackoutsDetails,
  dealOptionsDetails,
  importSteps,
  importedRoomTypes,
  inclusionRowsWithMissingAsset,
  inclusionsDetails,
  loadsCosts,
  loadsRrp,
  missingAssetMessages,
  reviewInclusionAssets,
  toBlackoutFormValues,
  toImportedDealOptions,
  toInclusionDrafts,
} from './excelImportModel';
import { readWorkbook } from './readWorkbook';
import {
  type RoomChoices,
  prefillRoomChoices,
  resolveRoomAssets,
  roomTypesSharingAnAsset,
  roomTypesWithMissingAsset,
} from './roomMapping';
import type { ParsedWorkbook } from './types';

const ACCEPTED_FILES = '.xlsx,.xlsm';

type ExcelImportDialogProps = {
  offerId: string;
  open: boolean;
  onClose: () => void;
};

export default function ExcelImportDialog({ offerId, open, onClose }: ExcelImportDialogProps) {
  const { replaceBlackouts } = useBlackoutsContext();
  const { optionStates, replaceFirstOption, roomAssets, addRoomAsset } = usePackageOptionsContext();
  const { values: propertySetup, fx } = usePropertySetupContext();
  const { inclusionAssets, replaceOptionDrafts, linkAssetByName } = useInclusionDraftsContext();
  const { enqueueSnackbar } = useSnackbar();
  const { data: offer, isLoading: isLoadingOffer, error: offerError } = useGetOffer(offerId);
  const {
    data: accountAssets,
    error: accountAssetsError,
    isFetching: isFetchingAssets,
  } = useGetAccountAssets(offer?.vendor_account_id, 'rooms', { enabled: open });
  const {
    data: accountServiceAssets,
    error: accountServiceAssetsError,
    isFetching: isFetchingServiceAssets,
  } = useGetAccountAssets(offer?.vendor_account_id, 'services', { enabled: open });

  const [fileName, setFileName] = useState<string | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [workbook, setWorkbook] = useState<ParsedWorkbook | null>(null);
  const [selected, setSelected] = useState<Set<ImportSectionKey>>(new Set());
  const [step, setStep] = useState<ImportStep>('workbook');
  const [pickedRooms, setPickedRooms] = useState<RoomChoices>({});
  const [leftOutInclusions, setLeftOutInclusions] = useState<Set<number>>(new Set());
  const [inclusionChoices, setInclusionChoices] = useState<InclusionAssetChoices>({});
  // A read still running when the dialog closes is dropped, so it cannot overwrite a file picked after reopening.
  const latestReadRef = useRef(0);

  const { draftIdBySfid } = usePricingToolSession();
  const assets = useMemo(
    () => collectRoomAssets(accountAssets ?? [], draftIdBySfid, roomAssets),
    [accountAssets, draftIdBySfid, roomAssets],
  );
  const roomTypes = useMemo(() => importedRoomTypes(workbook?.dealOptions ?? []), [workbook]);
  const prefilledRooms = useMemo(() => prefillRoomChoices(roomTypes, assets), [roomTypes, assets]);
  const roomsMissingAsset = roomTypesWithMissingAsset(pickedRooms, assets);
  const roomChoices = { ...prefilledRooms, ...omit(pickedRooms, roomsMissingAsset) };
  const sharingAnAsset = roomTypesSharingAnAsset(roomChoices);
  const inclusionAccountAssets = useMemo(
    () => collectRoomAssets(accountServiceAssets ?? [], draftIdBySfid),
    [accountServiceAssets, draftIdBySfid],
  );
  const includedInclusions = workbook?.inclusions.filter(({ row }) => !leftOutInclusions.has(row)) ?? [];
  const inclusionsMissingAsset = inclusionRowsWithMissingAsset(
    inclusionChoices,
    inclusionAssets,
    inclusionAccountAssets,
  );

  const layoutIssues = workbook?.issues.filter((issue) => issue.section === 'layout') ?? [];
  const sectionDetails: Record<ImportSectionKey, CopySectionDetails | undefined> = {
    dealOptions: workbook?.layout ? dealOptionsDetails(workbook, fx.currency) : undefined,
    inclusions: workbook?.layout ? inclusionsDetails(workbook, fx.currency, leftOutInclusions) : undefined,
    blackouts: workbook?.layout ? blackoutsDetails(workbook, propertySetup, formatDateISO()) : undefined,
  };
  const hasContent: Record<ImportSectionKey, boolean> = {
    dealOptions: (workbook?.dealOptions.length ?? 0) > 0,
    inclusions: (workbook?.inclusions.length ?? 0) > 0,
    blackouts: (workbook?.blackouts.length ?? 0) > 0,
  };
  const loadsDealOptions = selected.has('dealOptions');
  const loadsInclusions = selected.has('inclusions');
  // Rooms are matched against the account's, so they must have loaded or every room would be created again.
  const isLoadingRooms = isLoadingOffer || isFetchingAssets;
  const roomsError = offerError || accountAssetsError;
  // Until Property Setup loads its currency reads AUD, which would let costs in another currency through.
  const dealOptionsReady = !isLoadingRooms && !roomsError && !fx.isLoading;
  // Inclusions reuse the account's assets by name, and RRP only loads in an AUD session.
  const isLoadingInclusionAssets = isLoadingOffer || isFetchingServiceAssets;
  const inclusionAssetsError = offerError || accountServiceAssetsError;
  const inclusionsReady = !isLoadingInclusionAssets && !inclusionAssetsError && !fx.isLoading;
  const sectionsReady =
    selected.size > 0 &&
    [...selected].every((section) => hasContent[section]) &&
    (!loadsDealOptions || dealOptionsReady) &&
    (!loadsInclusions || inclusionsReady);
  const steps = importSteps(selected);
  const stepIndex = steps.indexOf(step);
  const stepComplete: Record<ImportStep, boolean> = {
    workbook: sectionsReady,
    rooms: sharingAnAsset.size === 0,
    inclusions: includedInclusions.length > 0,
    confirm: true,
  };
  const warning = replaceWarning(selected);
  const missingAssets = missingAssetMessages(
    loadsDealOptions ? roomsMissingAsset : [],
    loadsInclusions
      ? includedInclusions.filter(({ row }) => inclusionsMissingAsset.includes(row)).map(({ name }) => name)
      : [],
  );

  function resetChoices() {
    setSelected(new Set());
    setStep('workbook');
    setPickedRooms({});
    setLeftOutInclusions(new Set());
    setInclusionChoices({});
  }

  function handleContinue() {
    // Stores every choice, not just the changed ones, so a refetch after review cannot move a room.
    if (step === 'rooms') {
      setPickedRooms(roomChoices);
    }
    if (step === 'inclusions') {
      setInclusionChoices(reviewInclusionAssets(includedInclusions, inclusionAssets, inclusionAccountAssets));
    }
    setStep(steps[stepIndex + 1]);
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again after editing it still fires a change.
    event.target.value = '';
    if (!file) {
      return;
    }

    setFileName(file.name);
    setWorkbook(null);
    setReadError(null);
    resetChoices();
    setIsReading(true);
    const readId = ++latestReadRef.current;
    let read: ParsedWorkbook | null = null;
    let error: string | null = null;
    try {
      read = await readWorkbook(file);
    } catch {
      error = `Couldn't read ${file.name}. Check it's a Pricing Tool workbook saved as .xlsx or .xlsm.`;
    }
    if (readId !== latestReadRef.current) {
      return;
    }
    setIsReading(false);
    setWorkbook(read);
    setReadError(error);
  }

  function handleSectionChange(section: ImportSectionKey, checked: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(section);
      } else {
        next.delete(section);
      }
      return next;
    });
  }

  function handleLoad() {
    if (loadsDealOptions) {
      const { assetByRoomType, created } = resolveRoomAssets(roomTypes, roomChoices, assets);
      created.forEach(addRoomAsset);
      const imported = toImportedDealOptions(workbook.dealOptions, assetByRoomType, loadsCosts(workbook, fx.currency));
      replaceFirstOption(replaceWithPreviousPackages(optionStates[0] ?? DEFAULT_OPTION_STATE, imported));
    }
    if (loadsInclusions) {
      const drafts = toInclusionDrafts(includedInclusions, loadsRrp(fx.currency)).map((draft, index) => {
        const reviewed = inclusionAccountAssets.find(
          ({ id }) => id === inclusionChoices[includedInclusions[index].row],
        );
        return { ...draft, assetLink: linkAssetByName(draft.name, reviewed ? [reviewed] : []) };
      });
      replaceOptionDrafts(0, drafts);
    }
    if (selected.has('blackouts')) {
      replaceBlackouts(toBlackoutFormValues(workbook.blackouts));
    }
    enqueueSnackbar(`Loaded from ${fileName}`, { variant: 'success' });
    onClose();
  }

  function handleExited() {
    latestReadRef.current += 1;
    setIsReading(false);
    setFileName(null);
    setWorkbook(null);
    setReadError(null);
    resetChoices();
  }

  return (
    <Dialog
      fullWidth
      maxWidth={step === 'rooms' || step === 'inclusions' ? 'lg' : 'md'}
      onClose={onClose}
      open={open}
      slotProps={{ transition: { onExited: handleExited } }}
    >
      <DialogTitle>Upload Excel</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={stepIndex} sx={{ mb: 3 }}>
          {steps.map((importStep) => (
            <Step key={importStep}>
              <StepLabel>{IMPORT_STEP_LABELS[importStep]}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 'workbook' && (
          <Stack spacing={2}>
            <Stack alignItems="center" direction="row" spacing={2}>
              <Button component="label" disabled={isReading} startIcon={<UploadFileIcon />} variant="outlined">
                Choose workbook
                <input accept={ACCEPTED_FILES} hidden onChange={handleFileChange} type="file" />
              </Button>
              <Typography color="text.secondary" variant="body2">
                {fileName ?? 'Pricing Tool workbook, .xlsx or .xlsm'}
              </Typography>
            </Stack>

            {isReading && <LinearProgress />}

            {readError && <Alert severity="error">{readError}</Alert>}

            {layoutIssues.map((issue) => (
              <Alert key={issue.message} severity="error">
                {issue.message}
              </Alert>
            ))}

            {workbook?.layout && (
              <>
                <Typography color="text.secondary" variant="body2">
                  {`Loads from the "${workbook.layout.primarySheet}" sheet.`}
                </Typography>
                <FormGroup>
                  {IMPORT_SECTIONS.map((section) => (
                    <CopySectionOption
                      checked={selected.has(section.key)}
                      details={sectionDetails[section.key]}
                      key={section.key}
                      label={section.label}
                      name={section.key}
                      onChange={(event) => handleSectionChange(section.key, event.target.checked)}
                    />
                  ))}
                </FormGroup>
                {((loadsDealOptions && isLoadingRooms) ||
                  (loadsInclusions && isLoadingInclusionAssets) ||
                  ((loadsDealOptions || loadsInclusions) && fx.isLoading)) && <CircularProgress size={24} />}
                {loadsDealOptions && roomsError && (
                  <Alert severity="error">
                    Unable to load this property&apos;s rooms, so deal options can&apos;t be matched to them.
                  </Alert>
                )}
                {loadsInclusions && inclusionAssetsError && (
                  <Alert severity="error">
                    Unable to load this property&apos;s inclusion assets, so inclusions can&apos;t be matched to them.
                  </Alert>
                )}
              </>
            )}
          </Stack>
        )}

        {step === 'rooms' && workbook && (
          <ImportRoomsStep
            assets={assets}
            choices={roomChoices}
            dealOptions={workbook.dealOptions}
            onChange={(roomType, assetId) => setPickedRooms((current) => ({ ...current, [roomType]: assetId }))}
            roomTypes={roomTypes}
            sharingAnAsset={sharingAnAsset}
          />
        )}

        {step === 'inclusions' && workbook && (
          <ImportInclusionsStep
            inclusions={workbook.inclusions}
            leftOutRows={leftOutInclusions}
            onToggle={(row, included) =>
              setLeftOutInclusions((current) => {
                const next = new Set(current);
                if (included) {
                  next.delete(row);
                } else {
                  next.add(row);
                }
                return next;
              })
            }
            reusesAsset={(name) => !!findReusableInclusionAsset(name, inclusionAssets, inclusionAccountAssets)}
            withRrp={loadsRrp(fx.currency)}
          />
        )}

        {step === 'confirm' && (
          <>
            <Typography variant="body2">From {fileName}:</Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {IMPORT_SECTIONS.filter((section) => selected.has(section.key)).map((section) => (
                <Typography component="li" key={section.key} variant="body2">
                  {`${section.label}: ${sectionDetails[section.key]?.summary}`}
                </Typography>
              ))}
            </Box>
            {warning && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {warning}
              </Alert>
            )}
            {missingAssets.map((message) => (
              <Alert key={message} severity="error" sx={{ mt: 2 }}>
                {message}
              </Alert>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        {stepIndex > 0 && (
          <Button onClick={() => setStep(steps[stepIndex - 1])} variant="text">
            Back
          </Button>
        )}
        {step === 'confirm' ? (
          <Button disabled={missingAssets.length > 0} onClick={handleLoad} variant="contained">
            Load
          </Button>
        ) : (
          <Button disabled={!stepComplete[step]} onClick={handleContinue} variant="contained">
            Continue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
